import type { ComponentInit } from "@scripts/registry";

const FORM = "[data-fa-head-to-head-tile-form]";
const ROW = "[data-fa-head-to-head-tile-form-row]";
const CHIP = "[data-fa-head-to-head-tile-chip]";
const TIP = "[data-fa-head-to-head-tile-tip]";
const CENTRED = "fa-head-to-head-tile__result--tip-centred";
const OPEN = "fa-head-to-head-tile__result--tip-open";
const SHIFT = "--fa-head-to-head-tip-shift";

/* Serialised attribute, not style.length: after removeProperty the element still reads
   hasAttribute("style") with an empty value, and an emptied style="" prints in the panel. */
const clearShift = (chip: HTMLElement) => {
	chip.style.removeProperty(SHIFT);

	if (chip.getAttribute("style") === "") chip.removeAttribute("style");
};

export const initHeadToHeadForm: ComponentInit = (group) => {
	const chips = [...group.querySelectorAll<HTMLElement>(CHIP)];

	if (chips.length === 0) return;

	const bounds = group.closest<HTMLElement>(ROW) ?? group;
	const desktop = window.matchMedia("(width >= 64rem)");
	const hoverFine = window.matchMedia("(hover: hover) and (pointer: fine)");
	const controller = new AbortController();
	const { signal } = controller;

	const place = () => {
		if (desktop.matches) {
			for (const chip of chips) {
				chip.classList.remove(CENTRED);
				clearShift(chip);
			}

			return;
		}

		const box = bounds.getBoundingClientRect();

		for (const chip of chips) {
			const tip = chip.querySelector<HTMLElement>(TIP);

			if (tip === null) continue;

			const rect = chip.getBoundingClientRect();
			const middle = rect.left + rect.width / 2;
			const half = tip.getBoundingClientRect().width / 2;
			const fits = middle - half >= box.left && middle + half <= box.right;

			chip.classList.toggle(CENTRED, fits);

			if (fits) clearShift(chip);
			else chip.style.setProperty(SHIFT, `${box.right - rect.right}px`);
		}
	};

	place();

	/* The row's width, not a window listener: a phone's URL bar fires resize on every scroll
	   and never changes this width, so a quiet scroll re-measures nothing. */
	let lastWidth = bounds.getBoundingClientRect().width;
	const observer = new ResizeObserver(() => {
		const width = bounds.getBoundingClientRect().width;

		if (width === lastWidth) return;

		lastWidth = width;
		place();
	});

	observer.observe(bounds);

	const closeAll = () => {
		for (const chip of chips) chip.classList.remove(OPEN);
	};

	if (!hoverFine.matches) {
		group.addEventListener(
			"click",
			(event) => {
				const chip =
					event.target instanceof Element
						? event.target.closest<HTMLElement>(CHIP)
						: null;

				if (chip === null || !chips.includes(chip)) return;

				const opening = !chip.classList.contains(OPEN);

				closeAll();
				chip.classList.toggle(OPEN, opening);
			},
			{ signal },
		);

		/* pointerdown, not click: iOS only synthesises clicks over interactive targets, and
		   the page at large is not one — a tap on plain text would never close the tip. */
		document.addEventListener(
			"pointerdown",
			(event) => {
				if (event.target instanceof Element && event.target.closest(FORM) === group) return;

				closeAll();
			},
			{ signal },
		);

		document.addEventListener(
			"keydown",
			(event) => {
				if (event.key === "Escape") closeAll();
			},
			{ signal },
		);
	}

	return () => {
		controller.abort();
		observer.disconnect();

		for (const chip of chips) {
			chip.classList.remove(CENTRED, OPEN);
			clearShift(chip);
		}
	};
};
